import { Stack, Tooltip, Typography } from "@mui/material"
import { SxProps, Theme } from "@mui/material/styles"
import { SmallWarningIcon } from "@tc/ui-dfe/icons"
import { MPI_COLOR } from "../../ThemeProvider/color-options"

interface FormWarningPromptProps {
  message?: string
}

/**
 * Renders a warning icon that shows a styled tooltip on hover.
 * Used for non-blocking drools field-level warnings.
 */
export const FormWarningPrompt = ({ message }: FormWarningPromptProps) => {
  if (!message) return null

  return (
    <Tooltip
      title={
        <Typography
          variant="body2"
          sx={{
            p: 0.5,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "16px",
            letterSpacing: "-0.14px",
            whiteSpace: "pre-line",
          }}
        >
          {message}
        </Typography>
      }
      arrow
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: MPI_COLOR.paleYellow,
            color: (theme) => theme.palette.text.primary,
            borderRadius: "3px",
            maxWidth: 320,
            minHeight: 56,
            p: 1.5,
            boxShadow: "0px 10px 21px 0px #00000040",
            "& .MuiTooltip-arrow": {
              color: MPI_COLOR.paleYellow,
            },
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          cursor: "pointer",
          width: "fit-content",
          mt: 0.5,
        }}
      >
        <SmallWarningIcon
          sx={{
            width: 18,
            fill: MPI_COLOR.alertChrome,
          }}
        />
      </Stack>
    </Tooltip>
  )
}

/**
 * Returns sx overrides to apply a warning border to an MUI outlined input.
 * Use when there's no error but a warning exists.
 */
export const warningBorderSx = (
  hasError: boolean,
  fieldWarning?: string,
): SxProps<Theme> =>
  !hasError && !!fieldWarning
    ? {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: MPI_COLOR.alertChrome,
          borderWidth: 2,
        },
      }
    : {}
